if (!customElements.get('bio-showcase')) {
  customElements.define(
    'bio-showcase',
    class BioShowcase extends HTMLElement {
      connectedCallback() {
        this.scroller = this.querySelector('[data-bs-scroller]');
        this.boxes = [...this.querySelectorAll('[data-bs-box]')];
        this.layers = [...this.querySelectorAll('[data-bs-layer]')];
        this.copies = [...this.querySelectorAll('[data-bs-copy]')];
        this.dots = [...this.querySelectorAll('[data-bs-dot]')];
        this.ingredients = [...this.querySelectorAll('[data-bs-ingredient]')];
        this.prev = this.querySelector('[data-bs-prev]');
        this.next = this.querySelector('[data-bs-next]');
        this.active = 0;
        this.activeState = 0;
        this.frame = null;

        this.scroller.addEventListener('scroll', () => this.onScroll(), { passive: true });
        this.boxes.forEach((box, index) => box.addEventListener('click', () => this.goTo(index)));
        this.dots.forEach((dot, stateIndex) => {
          dot.addEventListener('click', () => {
            const first = this.boxes.findIndex((box) => Number(box.dataset.state) === stateIndex);
            if (first > -1) this.goTo(first);
          });
        });
        this.prev?.addEventListener('click', () => this.goTo(this.active - 1));
        this.next?.addEventListener('click', () => this.goTo(this.active + 1));
        this.updateArrows();

        this.onBlockSelect = (event) => {
          let index = this.boxes.findIndex((box) => box.contains(event.target));
          if (index === -1) {
            const stateIndex = this.copies.findIndex((copy) => copy.contains(event.target));
            index = this.boxes.findIndex((box) => Number(box.dataset.state) === stateIndex);
          }
          if (index > -1) this.goTo(index);
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);

        if (this.dataset.nudge === 'true' && 'IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (!entries.some((entry) => entry.isIntersecting)) return;
              this.observer.disconnect();
              if (this.active === 0) this.scroller.classList.add('is-nudging');
            },
            { threshold: 0.6 }
          );
          this.observer.observe(this.scroller);
          this.scroller.addEventListener('animationend', () => this.scroller.classList.remove('is-nudging'));
        }
      }

      disconnectedCallback() {
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        this.observer?.disconnect();
      }

      startOffset() {
        return parseFloat(getComputedStyle(this.scroller).scrollPaddingLeft) || 0;
      }

      onScroll() {
        this.scroller.classList.remove('is-nudging');
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          const position = this.scroller.scrollLeft + this.startOffset();
          let closest = 0;
          let distance = Infinity;
          this.boxes.forEach((box, index) => {
            const gap = Math.abs(box.offsetLeft - position);
            if (gap < distance) {
              distance = gap;
              closest = index;
            }
          });
          this.setActive(closest);
        });
      }

      goTo(index) {
        if (!this.boxes[index]) return;
        const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.scroller.scrollTo({ left: this.boxes[index].offsetLeft - this.startOffset(), behavior: smooth ? 'smooth' : 'auto' });
        this.setActive(index);
      }

      setActive(index) {
        if (index === this.active) return;
        this.active = index;
        [index - 1, index, index + 1].forEach((near) => this.layers[near]?.classList.add('is-ready'));
        const reveal = () => {
          this.layers.forEach((layer, layerIndex) => layer.classList.toggle('is-active', layerIndex === this.active));
        };
        requestAnimationFrame(() => requestAnimationFrame(reveal));
        this.boxes.forEach((box, boxIndex) => box.classList.toggle('is-active', boxIndex === index));
        this.updateArrows();

        const state = Number(this.boxes[index].dataset.state);
        const firstOfState = this.boxes.findIndex((box) => Number(box.dataset.state) === state);
        const showCopy = index === firstOfState;
        if (state !== this.activeState) {
          this.activeState = state;
          this.dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === state));
          this.ingredients.forEach((label, labelIndex) => {
            label.classList.toggle('is-active', labelIndex === state);
            if (labelIndex === state) {
              label.removeAttribute('aria-hidden');
            } else {
              label.setAttribute('aria-hidden', 'true');
            }
          });
        }
        this.copies.forEach((copy, copyIndex) => {
          const visible = showCopy && copyIndex === state;
          copy.classList.toggle('is-active', visible);
          if (visible) {
            copy.removeAttribute('aria-hidden');
          } else {
            copy.setAttribute('aria-hidden', 'true');
          }
        });
      }

      updateArrows() {
        if (this.prev) this.prev.disabled = this.active === 0;
        if (this.next) this.next.disabled = this.active === this.boxes.length - 1;
      }
    }
  );
}
